<template>
  <Dropdown
      ref="dropdownRef"
      :class="[inputGroupClass]"
      strategy="absolute"
  >
    <template #default="{ open, close }">
      <div class="timepicker" :class="{disabled}">
        <input
            v-model="time"
            type="text"
            class="form-control"
            autocomplete="off"
            spellcheck="false"
            placeholder="00:00"
            :disabled="disabled"
            :class="[inputClass, {
            'is-invalid': error,
            'form-control-sm': small,
            'disabled': disabled
          }]"
            @click="open"
            @focus="onFocus"
            @blur="onBlur"
        />

        <input type="hidden" :name="parsedName" :value="inputValue">

        <span v-if="is12Type"
              class="time-type"
              :class="{disabled}"
              @click="toggleTimeType"
        >
          {{ timeType }}
        </span>
      </div>
    </template>


    <template #content="{open, close}">
      <div class="options">
        <Scrollbar>
          <div class="option"
               v-for="option in payload"
               :key="option.value"
               @mouseup="onSelect(option, close)"
          >
            <span>{{ option.output }}</span>
          </div>
        </Scrollbar>
      </div>
    </template>
  </Dropdown>
</template>
<script setup>
import {reactive, ref, watch} from "vue";
import {useFormControl, useFormControlParser} from "@/js/Elements/Form/FormControl.js";
import FormControlProps from "@/js/Elements/Form/FormControlProps.js";
import Scrollbar from "@/js/Elements/Scrollbar.vue";
import Dropdown from "@/js/Elements/Dropdown.vue";
import moment from "moment";

const emit = defineEmits(['update:modelValue', 'blur', 'change', 'focus']);
const props = defineProps({
  ...FormControlProps
});
const { parsedPlaceholder, parsedName } = useFormControlParser(props);
const {
  error,
  inputValue
} = useFormControl(props, emit);

const AM = 'AM';
const PM = 'PM'
const now = moment();
const currentDate = now.format('YYYY-MM-DD');
const currentTime = now.format('LT');
let modelValue = null;
let modelValueTime = null;

const time = ref( null);
const dropdownRef = ref();
const inputRef = ref();
const is12Type = ref(currentTime.match(/(AM|PM)/));
const timeType = ref(null);
const payload = reactive(createPayload());
let ignoreWatcher = false;


set(props.modelValue);
watch(() => props.modelValue, (value) => {
  if (!value) return;
  set(value);
});

watch(() => time.value, (n, o) => {
  if (ignoreWatcher) {
    ignoreWatcher = false;
    return;
  }
  checkValue(n, o);
});

function set(value) {
  modelValue = moment(`${currentDate} ${value || currentTime}`);
  modelValueTime = modelValue.format('HH:mm A');
  time.value = props.modelValue ? modelValue.format('LT').replace(/\s+(AM|PM)/, '') : null;
  timeType.value = modelValueTime.split(' ')[1].toUpperCase();
}

function onChange() {
  if (typeof time.value !== 'undefined' && time.value !== null && time.value.length < 5) return;

  formatAndEmit(time.value);

  if (!dropdownRef.value) return;
  dropdownRef.value.close();
}

function onBlur(e) {
  fix();
  emit('blur', e);
}

function onFocus(e) {
  emit('focus', e);
  if (!dropdownRef.value) return;
  dropdownRef.value.open();
}

function onSelect(option, close) {
  timeType.value = option.type;
  time.value = option.output.replace(/(AM|PM)/, '');
  formatAndEmit(time.value);
  close();
}

function toggleTimeType() {
  if (props.disabled) return;
  timeType.value = timeType.value === AM ? PM : AM;
  formatAndEmit(time.value);
}

function formatAndEmit(time) {
  if (!is12Type.value) {
    const h = time.split(':')[0];
    timeType.value = h >= 12 ? PM : AM;
  }
  const value = moment(`${currentDate} ${time} ${timeType.value}`, 'YYYY-MM-DD hh:mm A');
  inputValue.value = value.format('HH:mm');
  emit('change', value.format('HH:mm'));
}

function clear() {
  time.value = null;
}

function createPayload() {
  const list = [];
  const now = moment();

  for (let hour = 0; hour < 24; hour++) {
    now.set('hour', hour);


    for (let min = 0; min < 60; min += 5) {
      now.set('minute', min);

      list.push({
        output: now.format('LT'),
        value: now.format('HH:mm'),
        type: hour > 12 ? PM : AM
      });
    }
  }

  return list;
}

function checkValue(value, oldValue) {
  if (value === null || typeof value === 'undefined') return;

  if (typeof oldValue === 'undefined' || oldValue === null) {
    oldValue = value;
  }

  let raw = value.replace(/[^\d:]/g, '');

  if (raw.length < oldValue.length) {
    time.value = raw;
    onChange();
    return;
  }

  if (/^\d{3,4}$/.test(raw) && !raw.includes(':')) {
    raw = raw.slice(0, 2) + ':' + raw.slice(2);
  }

  const parts = raw.split(':');
  let hours = parseInt(parts[0]) || 0;
  let minutes = parts[1] !== undefined ? parseInt(parts[1]) || 0 : null;

  let hoursStr = null;
  let minutesStr = null;

  if (is12Type.value) {
    if (hours > 12) hours = 12;
    if (hours < 1) hours = 1;
    if (minutes !== null && minutes > 59) minutes = 59;

    hoursStr = parts[0].slice(0, 2);
    if (hoursStr.length === 2 || hours > 9) {
      hoursStr = hours.toString().padStart(2, '0');
    } else {
      hoursStr = hours.toString();
    }
  } else {
    if (hours > 23) hours = 23;
    if (minutes !== null && minutes > 59) minutes = 59;

    hoursStr = parts[0].slice(0, 2);
    if (hoursStr.length === 2) hoursStr = hours.toString().padStart(2, '0');
  }

  minutesStr = parts[1] ? parts[1].slice(0, 2) : '';
  if (minutesStr.length === 2) {
    minutesStr = minutes.toString().padStart(2, '0');
  }

  if (parts.length === 2) {
    raw = `${hoursStr}:${minutesStr}`;
  } else {
    raw = hoursStr;
  }

  time.value = raw;
  onChange();
}

function fix() {
  if (!time.value) return;

  const cleaned = time.value.replace(/[^\d:]/g, '');
  const parts = cleaned.split(':');

  let hours = parseInt(parts[0]) || 0;
  let minutes = parts[1] !== undefined ? parseInt(parts[1]) || 0 : 0;

  if (hours > 23) hours = 23;
  if (minutes > 59) minutes = 59;

  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');

  time.value = `${hoursStr}:${minutesStr}`;
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";
.timepicker {
  display: flex;
  align-items: center;

  &.disabled {
    background-color: $input-disabled-bg;
  }
}

.options {
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  max-height: toRem(300);
}

.option {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 100%;
  overflow: hidden;
  padding: spacing(1) spacing(5) spacing(1) spacing(3);
  font-size: fontSize(4);
  cursor: pointer;
  
  &:hover {
    background-color: themeColor('neutral-100');
  }

  > span {
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.time-type {
  cursor: pointer;
  font-size: fontSize(5);
  color: themeColor('neutral-600');
  font-weight: $font-weight-medium;
  position: relative;
  left: toRem(-11);
  top: toRem(-1.5);
}
</style>